/**
 * SummaryWeatherRow - Shows weather forecast inside the AI summary card.
 * Morning brief shows today's weather, evening recap shows tomorrow's.
 * Always renders — shows dynamic error message when weather is unavailable.
 */

import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { Colors } from '@/constants/theme';
import { useWeatherForDate } from '@/hooks/use-weather';
import { getWeatherIconColor } from '@/lib/weather/WeatherIconColors';
import type { SummaryVariant } from '@/types/home-summary';

type SummaryWeatherRowProps = {
  variant: SummaryVariant;
  palette: typeof Colors.light;
};

function getWeatherDate(variant: SummaryVariant): string {
  const now = new Date();
  if (variant === 'evening-recap') {
    now.setDate(now.getDate() + 1);
  }
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function SummaryWeatherRow({ variant, palette }: SummaryWeatherRowProps) {
  const date = getWeatherDate(variant);
  const { forecast, isLoading, error } = useWeatherForDate(date);

  const dayLabel = variant === 'morning-brief' ? 'Today' : 'Tomorrow';

  return (
    <View style={styles.wrapper}>
      {isLoading ? (
        <View style={styles.forecastRow}>
          <Ionicons
            name="cloud-outline"
            size={16}
            color={palette.textMuted}
          />
          <Text style={[styles.bodyText, { color: palette.text, fontFamily: getBodyFont() }]}>
            {dayLabel}
          </Text>
          <ActivityIndicator size="small" color={palette.textMuted} />
        </View>
      ) : forecast ? (
        <View style={styles.forecastRow}>
          <Ionicons
            name={forecast.icon as any}
            size={16}
            color={getWeatherIconColor(forecast.icon)}
          />
          <Text style={[styles.bodyText, { color: palette.text, fontFamily: getBodyFont() }]}>
            {dayLabel}
          </Text>
          <Text style={[styles.bodyText, { color: palette.text, fontFamily: getBodyFont() }]}>
            {forecast.high}° / {forecast.low}°
          </Text>
          <Text
            style={[styles.bodyText, { color: palette.textMuted, fontFamily: getBodyFont() }]}
            numberOfLines={1}
          >
            {forecast.description}
          </Text>
          {forecast.precipChance > 20 && (
            <View style={styles.precipBadge}>
              <Ionicons name="water-outline" size={12} color={palette.primary} />
              <Text style={[styles.precipText, { color: palette.primary, fontFamily: getBodyFont() }]}>
                {forecast.precipChance}%
              </Text>
            </View>
          )}
        </View>
      ) : error ? (
        <Text style={[styles.bodyText, { color: palette.textMuted, fontFamily: getBodyFont(), fontStyle: 'italic' }]}>
          Unavailable ({error.message})
        </Text>
      ) : null}
      <View style={[styles.separator, { backgroundColor: palette.border }]} />
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    gap: Spacing.sm,
  },
  forecastRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  bodyText: {
    fontSize: 14,
    lineHeight: 22,
  },
  precipBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  precipText: {
    fontSize: 14,
    lineHeight: 22,
    fontWeight: '600',
  },
  separator: {
    height: StyleSheet.hairlineWidth,
  },
});
